import React, { FC } from "react";
import { Box, MenuItem, TextField, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { HAMASPIK_CONTROL_HEIGHT } from "../../../../../assets/hamaspik/theme";
import { PostIfLookupItemDto, PostIfPositionRulesDto } from "../../../../../types/pifDto";
import { pifFieldId } from "../../pifFocus";
import { getPifPayBandAxis, getPifRateVerdict, isBound, isPifStartDateNotesRequired } from "../../pifValidation";
import { PifMenuSelect } from "../../pifSelectMenu";
import { PifFormValues, toDecimalInput, toSelectId } from "../../types";
import PifField, { PifFieldLabel } from "./PifField";
import PifSectionCard from "./PifSectionCard";

interface PifSectionPayProps {
  values: PifFormValues;
  onChange: (patch: Partial<PifFormValues>) => void;
  rules: PostIfPositionRulesDto | null;
  rateTypes: PostIfLookupItemDto[];
}

const PifSectionPay: FC<PifSectionPayProps> = ({ values: f, onChange, rules, rateTypes }) => {
  const verdict = getPifRateVerdict(f.rate, rules);
  // Bug 82665: a past Intended start date (Position card) requires an explanation here — the
  // warning up there and the requirement down here must agree on what "past" means, hence the
  // shared isPifStartDateNotesRequired rather than each card computing its own.
  const notesRequired = isPifStartDateNotesRequired(f.startDate);
  const notesMissing = notesRequired && !f.notes.trim();

  // Bug 82694: the bar is drawn against every bound the API supplied, not the position band
  // alone. A position with no market rate on file used to fail this gate outright and render an
  // empty track with no marker, so typing a rate gave no feedback at all. A null axis now means
  // genuinely nothing is known - draw no track rather than a dead one.
  const axis = getPifPayBandAxis(rules);
  const clamp = (v: number) => Math.max(0, Math.min(100, v));
  const pct = (value: number) => (axis ? clamp(((value - axis.min) / axis.span) * 100) : 0);

  const positionMin = isBound(rules?.positionMin) ? rules.positionMin : null;
  const positionMax = isBound(rules?.positionMax) ? rules.positionMax : null;
  const hiringMin = isBound(rules?.hiringMin) ? rules.hiringMin : null;
  const hiringMax = isBound(rules?.hiringMax) ? rules.hiringMax : null;

  // The hiring band highlighted within the axis. When the axis IS the hiring band (no position
  // band on file) the fill spans the whole track, which is the correct reading of it.
  const hLeft = hiringMin !== null ? pct(hiringMin) : 0;
  const hRight = hiringMax !== null ? pct(hiringMax) : 0;
  const showHiring = !!axis && hiringMin !== null && hiringMax !== null && hRight > hLeft;

  // No axis but a position IS picked means nothing is on file to scale against. Draw the track
  // full-width with the marker centred rather than hiding it - an absent bar reads as a graphic
  // that failed to load (review on Bug 82694). The centre is the only honest place for the
  // marker when there are no bounds, and the scale row is dropped because there is nothing to
  // label. The message below is what actually says the band is missing.
  const noBand = !axis && !!rules;
  const showTrack = !!rules;
  const showFill = noBand || showHiring;
  const fillLeft = noBand ? 0 : hLeft;
  const fillWidth = noBand ? 100 : hRight - hLeft;

  const rateNum = parseFloat(f.rate);
  const showMarker = showTrack && verdict.hasRate;
  const rLeft = noBand ? 50 : pct(rateNum);

  // Empty in the no-band case: with nothing on file there is no range to name, and the card
  // reads cleaner with just the bar (review on Bug 82694).
  const rangeLabel = !rules
    ? "Pick a position"
    : positionMin !== null && positionMax !== null
      ? `Position $${positionMin}–$${positionMax}`
      : hiringMin !== null && hiringMax !== null
        ? `Hiring $${hiringMin}–$${hiringMax}`
        : "";

  // Bug 82798: captured here, beside Rate, so creation matches the view-only
  // Compensation and schedule section. The part-time warning still reads empType
  // from the Position card — that toggle stays there.
  const hoursNum = parseFloat(f.hours);
  const hoursWarn = !isNaN(hoursNum) && ((f.empType === "Part-time" && hoursNum > 30) || hoursNum > 60);

  const bandMsgColor = !rules || !verdict.hasRate
    ? "text.secondary"
    : verdict.blocked
      ? "error.main"
      : verdict.warn
        ? "warning.dark"
        : "success.dark";

  return (
    <PifSectionCard id="sec-pay" title="Pay">
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 2, alignItems: "start" }}>
        <PifField label="Rate type *">
          <PifMenuSelect
            size="small"
            fullWidth
            displayEmpty
            value={f.rateTypeId ?? ""}
            onChange={(e) => {
              const id = toSelectId(e.target.value);
              onChange({ rateTypeId: id, rateType: rateTypes.find((r) => r.id === id)?.text ?? "" });
            }}
            // On the display button, not the hidden input, so a validation error can focus it.
            SelectDisplayProps={{ id: pifFieldId("rateTypeId") }}
          >
            <MenuItem value="">Please select</MenuItem>
            {rateTypes.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.text}
              </MenuItem>
            ))}
          </PifMenuSelect>
        </PifField>
        <PifField label="Rate (USD) *">
          <TextField
            id={pifFieldId("rate")}
            size="small"
            fullWidth
            value={f.rate}
            inputMode="decimal"
            placeholder="0.00"
            error={verdict.blocked}
            onChange={(e) => onChange({ rate: toDecimalInput(e.target.value, 15) })}
            data-testid="postif-rate"
          />
          {verdict.blocked && (
            <Typography variant="caption" color="error.main" data-testid="postif-rate-error">
              {verdict.message}
            </Typography>
          )}
        </PifField>
        <PifField label="Hours per week *">
          <TextField
            id={pifFieldId("hours")}
            size="small"
            fullWidth
            value={f.hours}
            inputMode="decimal"
            placeholder="e.g. 35.5"
            onChange={(e) => onChange({ hours: toDecimalInput(e.target.value) })}
          />
          {hoursWarn && (
            <Typography variant="caption" color="warning.main">
              {f.empType === "Part-time" ? "Over 30 hours on a part-time line HR will query this." : "That's a lot of hours. Double-check."}
            </Typography>
          )}
        </PifField>
        <Box
          sx={{
            p: 1.875,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.25, alignItems: "baseline" }}>
            <Typography variant="caption" fontWeight={700} letterSpacing="0.08em" color="text.secondary">
              PAY BANDS
            </Typography>
            {rangeLabel && (
              <Typography variant="caption" color="text.secondary">
                {rangeLabel}
              </Typography>
            )}
          </Box>
          {showTrack && (
            <>
              <Box sx={{ py: 0.5 }}>
                <Box
                  sx={{ position: "relative", height: 12, bgcolor: "grey.200", borderRadius: 999, overflow: "visible" }}
                  data-testid="postif-pay-band"
                >
                  {showFill && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        left: `${fillLeft}%`,
                        width: `${fillWidth}%`,
                        bgcolor: "#F8A87E",
                        borderRadius: 999,
                      }}
                      data-testid="postif-pay-band-fill"
                    />
                  )}
                  {showMarker && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: -4,
                        bottom: -4,
                        left: `calc(${rLeft}% - 2px)`,
                        width: 4,
                        borderRadius: 999,
                        bgcolor: verdict.blocked ? "error.main" : verdict.warn ? "warning.main" : "success.main",
                      }}
                      data-testid="postif-pay-band-marker"
                    />
                  )}
                </Box>
              </Box>
              {axis && (
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="caption" color="text.secondary">
                    ${axis.min}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ${axis.max}
                  </Typography>
                </Box>
              )}
            </>
          )}
          <Typography variant="caption" color={bandMsgColor} sx={{ lineHeight: 1.45 }}>
            {verdict.message}
          </Typography>
        </Box>
      </Box>

      {rules?.mayOperateVehicle && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3.25, alignItems: "flex-start", pt: 1.75, borderTop: "1px solid", borderColor: "divider" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <PifFieldLabel label="Will operate an agency vehicle?" />
            <Box sx={{ display: "flex", gap: 1.25, "& .MuiButton-root": { minHeight: HAMASPIK_CONTROL_HEIGHT } }}>
              <Button variant={f.vehicle === "yes" ? "contained" : "outlined"} size="small" onClick={() => onChange({ vehicle: "yes" })}>
                Yes
              </Button>
              <Button
                variant={f.vehicle === "no" ? "contained" : "outlined"}
                color={f.vehicle === "no" ? "secondary" : "primary"}
                size="small"
                onClick={() => onChange({ vehicle: "no" })}
              >
                No
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 1.75, borderTop: "1px solid", borderColor: "divider" }}>
        <PifFieldLabel label={notesRequired ? "Notes for the approvers *" : "Notes for the approvers"} />
        <TextField
          id={pifFieldId("notes")}
          size="small"
          fullWidth
          multiline
          rows={2}
          value={f.notes}
          placeholder="Anything unusual about the rate, the schedule, or the start date."
          error={notesMissing}
          onChange={(e) => onChange({ notes: e.target.value })}
        />
        {notesMissing && (
          <Typography variant="caption" color="error.main">
            Required — the intended start date is in the past, so say why here.
          </Typography>
        )}
      </Box>
    </PifSectionCard>
  );
};

export default PifSectionPay;
